import React from 'react';
import { mockTopBuyersData, fetchTopBuyersSummary, fetchTopBuyers } from '../data/mockData';
import EthAmount from '../components/EthAmount';
import './TopBuyers.css';

const TopBuyers: React.FC = () => {
  const [details, setDetails] = React.useState(mockTopBuyersData.details);
  const [pagination, setPagination] = React.useState(mockTopBuyersData.pagination);
  const [page, setPage] = React.useState(pagination.currentPage || 1);
  const [summary, setSummary] = React.useState(mockTopBuyersData.summary);
  const [customPrice, setCustomPrice] = React.useState('');

  React.useEffect(() => {
    let isMounted = true;
    const loadSummary = async () => {
      try {
        const s = await fetchTopBuyersSummary();
        if (isMounted) setSummary(s);
      } catch {}
    };
    loadSummary();
    return () => { isMounted = false; };
  }, []);

  // Load details when page changes
  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { details, pagination } = await fetchTopBuyers(page, 10);
        if (!isMounted) return;
        setDetails(details);
        setPagination(pagination);
      } catch {}
    })();
    return () => { isMounted = false; };
  }, [page]);

  // Parse helpers
  const parseTokenPrice = (value: string): number => {
    // Supports formats like "0.0(6)57999" => 0.00000057999
    const trimmed = String(value).trim();
    const special = trimmed.match(/^0\.0\((\d+)\)(\d+)$/);
    if (special) {
      const zeros = Number(special[1]);
      const digits = special[2];
      const asStr = `0.${'0'.repeat(isFinite(zeros) ? zeros : 0)}${digits}`;
      const num = Number(asStr);
      return isNaN(num) ? 0 : num;
    }
    const num = parseFloat(trimmed.replace(/[$,]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const parseHoldings = (value: string): number => {
    // Expect formats like "886,412.57 (0.00%)" => 886412.57
    const amountPart = String(value).split(' ')[0] || '';
    const num = parseFloat(amountPart.replace(/,/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const handleCustomPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setCustomPrice(input);

    // Compute potentialLossInCustomPrice based on formula
    const currentHoldings = parseHoldings(summary.tokenInUsersWallet);
    if (currentHoldings > 0) {
      const custom = parseFloat(input.replace(/[$,]/g, ''));
      const validCustom = isNaN(custom) ? 0 : custom;
      const lossAtCustom = ( validCustom) * currentHoldings;
      setSummary(prev => ({ ...prev, potentialLossInCustomPrice: lossAtCustom }));
    } else {
      setSummary(prev => ({ ...prev, potentialLossInCustomPrice: 0 }));
    }
  };

  // Helper function to safely convert values to numbers, defaulting to 0 for NaN
  const safeNumber = (value: string | number): number => {
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return isNaN(value) ? 0 : value;
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage === page || nextPage > pagination.totalPages) return;
    setPage(nextPage);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const { currentPage, totalPages } = pagination;

    // Previous button
    buttons.push(
      <button
        key="prev"
        className="pagination-btn"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        &lt;
      </button>
    );

    // Page numbers
    if (currentPage > 3) {
      buttons.push(
        <button key="1" className="pagination-btn" onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
    }

    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages - 2) {
      buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      buttons.push(
        <button key={totalPages} className="pagination-btn" onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        className="pagination-btn"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        &gt;
      </button>
    );

    return buttons;
  };

  return (
    <div className="top-buyers-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="title-icon top-buyers"></span>
          Top buyers
        </h1>
      </div>

      <div className="content">
        {/* Summary Section */}
        <div className="summary-section">
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-title">ETH in user's wallet</div>
              <div className="card-value">
                <EthAmount amount={summary.ethInUsersWallet} />
              </div>
            </div>
            
            <div className="summary-card">
              <div className="card-title">Token price</div>
              <div className="card-value">
                <span className="eth-icon"><span className="eth-icon"></span></span>
                {summary.tokenPrice}
              </div>
              <div className="price-input-container">
                <input 
                  type="text" 
                  placeholder="Input price (x)"
                  className="price-input"
                  value={customPrice}
                  onChange={handleCustomPriceChange}
                />
              </div>
            </div>
            
            <div className="summary-card">
              <div className="card-title">Token in user's wallet</div>
              <div className="card-value">{summary.tokenInUsersWallet}</div>
            </div>
            
            <div className="summary-card">
              <div className="card-title">Potential Loss</div>
              <div className="card-value">
                <EthAmount amount={summary.potentialLoss} />
              </div>
            </div>
            
            <div className="summary-card">
              <div className="card-title">Potential Loss in custom price</div>
              <div className="card-value">
                <EthAmount amount={summary.potentialLossInCustomPrice} />
              </div>
            </div>
          </div>

        </div>

        {/* Detail Table */}
        <div className="detail-section">
          <h3 className="detail-title">Detail</h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    Address
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    Bought
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    Sold
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    P&L
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    ETH
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    WETH
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    Token
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="eth-icon"><span className="eth-icon"></span></span>
                    Potential Loss
                    <span className="sort-icon"></span>
                  </th>
                  <th>
                    <span className="wallet-icon"></span>
                    Wallet
                    <span className="sort-icon"></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {details.map((buyer, index) => (
                  <tr key={index}>
                    <td>
                      <div className="address-cell">
                        {buyer.address}
                        <span 
                          className="copy-icon"
                          onClick={() => handleCopy(buyer.address)}
                          title="Copy address"
                        >
                          📋
                        </span>
                      </div>
                    </td>
                    <td><EthAmount amount={safeNumber(buyer.bought)} className="compact" /></td>
                    <td><EthAmount amount={safeNumber(buyer.sold)} className="compact" /></td>
                    <td>
                      <EthAmount 
                        amount={safeNumber(buyer.profitAndLoss)} 
                        className="compact" 
                        colorClass={typeof buyer.profitAndLoss === 'string' && buyer.profitAndLoss.startsWith('-') ? 'red-text' : 'green-text'}
                      />
                    </td>
                    <td><EthAmount amount={safeNumber(buyer.eth)} className="compact" /></td>
                    <td><EthAmount amount={safeNumber(buyer.weth)} className="compact" /></td>
                    <td>{buyer.token}</td>
                    <td><EthAmount amount={safeNumber(buyer.potentialLoss)} className="compact" /></td>
                    <td>
                      <span className="wallet-icon"></span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <div className="pagination-info">
              Total {pagination.totalItems} items
            </div>
            <div className="pagination-controls">
              {renderPaginationButtons()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBuyers;
